/**
 * REGLAS DE NEGOCIO - GESTIÓN DE EMBUDOS (ADMINISTRACIÓN)
 * ========================================================
 * 
 * 1. ELIMINACIÓN DE EMBUDOS:
 *    - Solo se pueden eliminar embudos que NO tengan leads asignados
 *    - Si el embudo tiene leads, se debe mostrar un error con la cantidad
 *    - La validación se hace tanto en frontend (state.leads) como en backend
 *    - Endpoint: DELETE /api/pipelines/{pipeline_id}
 * 
 * 2. ETAPAS OBLIGATORIAS (LOCKED_STAGE_IDS):
 *    - Nuevo Lead, Propuesta, Negociación, Perdido, Ganado
 *    - No se pueden eliminar ni renombrar
 *    - Se puede cambiar el color pero no el ID ni nombre
 * 
 * 3. ETAPAS PERSONALIZADAS:
 *    - Mínimo 1 etapa personalizada por embudo
 *    - Se pueden arrastrar para reordenar (excepto las obligatorias)
 *    - No se pueden mover a la posición de una etapa obligatoria
 * 
 * 4. CAMPOS PERSONALIZADOS:
 *    - Cada embudo puede tener sus propios campos personalizados
 *    - Se configuran desde la sección de edición del embudo
 */

import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
  Plus, Edit2, Trash2, GitBranch, GripVertical, X, Save, 
  ChevronDown, ChevronUp, Palette, ExternalLink, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import InfoTooltip from '../components/ui/InfoTooltip';
import * as api from '../services/api';

const STAGE_COLORS = [
  '#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#22c55e',
  '#ef4444', '#ec4899', '#06b6d4', '#6366f1', '#84cc16'
];

// Opciones de período unificadas
const PERIOD_OPTIONS = [
  { value: 'week', label: 'Esta Semana' },
  { value: 'month', label: 'Este Mes' },
  { value: 'quarter', label: 'Este Trimestre' },
  { value: 'semester', label: 'Este Semestre' },
  { value: 'year', label: 'Este Año' },
  { value: 'all', label: 'Todo el Tiempo' },
  { value: 'custom', label: 'Rango Personalizado' },
];

// Etapas obligatorias que no se pueden editar ni eliminar
const REQUIRED_STAGES = [
  { id: 'nuevo-lead', name: 'Nuevo Lead', color: '#3B82F6', order: 0, required: true },
  { id: 'propuesta', name: 'Propuesta', color: '#F59E0B', order: 2, required: true },
  { id: 'negociacion', name: 'Negociación', color: '#10B981', order: 3, required: true },
  { id: 'perdido', name: 'Perdido', color: '#EF4444', order: 4, required: true },
  { id: 'cerrado', name: 'Ganado', color: '#22C55E', order: 5, required: true }
];

// IDs de etapas que no se pueden modificar
const LOCKED_STAGE_IDS = ['nuevo-lead', 'propuesta', 'negociacion', 'perdido', 'cerrado'];

const PipelineManager = () => {
  const { state, actions } = useData();
  const { settings } = useSettings();
  const navigate = useNavigate();
  
  // Dynamic primary color
  const primaryColor = settings?.primaryColor || '#00b30c';
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);
  const [deletingPipeline, setDeletingPipeline] = useState(null);
  const [expandedPipeline, setExpandedPipeline] = useState(null);
  const [draggedStageIndex, setDraggedStageIndex] = useState(null);
  
  // Date filter state
  const [period, setPeriod] = useState('year');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [pipelineStats, setPipelineStats] = useState({});
  
  // Calculate period dates
  const getPeriodDates = () => {
    const now = new Date();
    let startDate;
    
    switch (period) {
      case 'week':
        const dayOfWeek = now.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now);
        startDate.setDate(now.getDate() - daysToMonday);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'semester':
        const semester = now.getMonth() < 6 ? 0 : 6;
        startDate = new Date(now.getFullYear(), semester, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'all':
        return { startDate: null, endDate: null };
      case 'custom':
        return {
          startDate: customDateFrom ? new Date(customDateFrom) : null,
          endDate: customDateTo ? new Date(customDateTo + 'T23:59:59') : now
        };
      default:
        startDate = new Date(now.getFullYear(), 0, 1);
    }
    
    return { startDate, endDate: now };
  };
  
  // Load pipeline stats when period changes
  useEffect(() => {
    const loadStats = async () => {
      const stats = {};
      for (const pipeline of state.pipelines) {
        try {
          const response = await api.getPipelineStatistics(pipeline.id, period, period === 'custom' ? customDateFrom : null, period === 'custom' ? customDateTo : null);
          stats[pipeline.id] = response;
        } catch (error) {
          console.error('Error loading stats for pipeline:', pipeline.id, error);
        }
      }
      setPipelineStats(stats);
    };
    
    if (state.pipelines.length > 0) {
      loadStats();
    }
  }, [state.pipelines, period, customDateFrom, customDateTo]);
  
  // Form state for creating/editing pipeline
  const [pipelineForm, setPipelineForm] = useState({
    name: '',
    description: '',
    stages: [
      { id: 'nuevo-lead', name: 'Nuevo Lead', color: '#3B82F6', order: 0, required: true },
      { id: `stage-${Date.now()}-1`, name: 'Contactado', color: '#8B5CF6', order: 1 },
      { id: 'propuesta', name: 'Propuesta', color: '#F59E0B', order: 2, required: true },
      { id: 'negociacion', name: 'Negociación', color: '#10B981', order: 3, required: true },
      { id: 'perdido', name: 'Perdido', color: '#EF4444', order: 4, required: true },
      { id: 'cerrado', name: 'Ganado', color: '#22C55E', order: 5, required: true },
    ]
  });

  const resetForm = () => {
    setPipelineForm({
      name: '',
      description: '',
      stages: [
        { id: 'nuevo-lead', name: 'Nuevo Lead', color: '#3B82F6', order: 0, required: true },
        { id: `stage-${Date.now()}-1`, name: 'Contactado', color: '#8B5CF6', order: 1 },
        { id: 'propuesta', name: 'Propuesta', color: '#F59E0B', order: 2, required: true },
        { id: 'negociacion', name: 'Negociación', color: '#10B981', order: 3, required: true },
        { id: 'perdido', name: 'Perdido', color: '#EF4444', order: 4, required: true },
        { id: 'cerrado', name: 'Ganado', color: '#22C55E', order: 5, required: true },
      ]
    });
  };

  const handleOpenCreate = () => {
    resetForm();
    setEditingPipeline(null);
    setShowCreateModal(true);
  };

  const handleOpenEdit = (pipeline) => {
    setPipelineForm({
      name: pipeline.name,
      description: pipeline.description || '',
      stages: pipeline.stages.map((s, i) => ({ ...s, order: i }))
    });
    setEditingPipeline(pipeline);
    setShowCreateModal(true);
  };

  const handleAddStage = () => {
    const newStage = {
      id: `stage-${Date.now()}`,
      name: `Etapa ${pipelineForm.stages.length + 1}`,
      color: STAGE_COLORS[pipelineForm.stages.length % STAGE_COLORS.length],
      order: pipelineForm.stages.length
    };
    setPipelineForm({
      ...pipelineForm,
      stages: [...pipelineForm.stages, newStage]
    });
  };

  const handleRemoveStage = (stageId) => {
    // No permitir eliminar etapas obligatorias
    if (LOCKED_STAGE_IDS.includes(stageId)) {
      toast.error('Esta etapa es obligatoria y no se puede eliminar');
      return;
    }
    
    // Contar etapas no obligatorias
    const customStages = pipelineForm.stages.filter(s => !LOCKED_STAGE_IDS.includes(s.id));
    if (customStages.length <= 0 && !LOCKED_STAGE_IDS.includes(stageId)) {
      toast.error('Debe haber al menos una etapa personalizada');
      return;
    }
    
    setPipelineForm({
      ...pipelineForm,
      stages: pipelineForm.stages.filter(s => s.id !== stageId).map((s, i) => ({ ...s, order: i }))
    });
  };

  const handleStageChange = (stageId, field, value) => {
    // No permitir cambiar nombre de etapas obligatorias
    if (LOCKED_STAGE_IDS.includes(stageId) && field === 'name') {
      toast.error('El nombre de esta etapa no se puede modificar');
      return;
    }
    
    setPipelineForm({
      ...pipelineForm,
      stages: pipelineForm.stages.map(s => 
        s.id === stageId ? { ...s, [field]: value } : s
      )
    });
  };

  // Drag and Drop handlers for stages
  const handleDragStart = (e, index) => {
    const stage = pipelineForm.stages[index];
    // Don't allow dragging locked stages
    if (LOCKED_STAGE_IDS.includes(stage.id)) {
      e.preventDefault();
      return;
    }
    setDraggedStageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedStageIndex === null || draggedStageIndex === dropIndex) {
      setDraggedStageIndex(null);
      return;
    }

    const targetStage = pipelineForm.stages[dropIndex];
    // Don't allow dropping onto locked stages position
    if (LOCKED_STAGE_IDS.includes(targetStage.id)) {
      toast.error('No puedes mover una etapa a la posición de una etapa obligatoria');
      setDraggedStageIndex(null);
      return;
    }

    const newStages = [...pipelineForm.stages];
    const [draggedStage] = newStages.splice(draggedStageIndex, 1);
    newStages.splice(dropIndex, 0, draggedStage);
    
    // Update order
    const updatedStages = newStages.map((stage, i) => ({ ...stage, order: i }));
    
    setPipelineForm({
      ...pipelineForm,
      stages: updatedStages
    });
    
    setDraggedStageIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedStageIndex(null);
  };

  const handleSubmit = async () => {
    if (!pipelineForm.name.trim()) {
      toast.error('El nombre del embudo es requerido');
      return;
    }

    if (pipelineForm.stages.length === 0) {
      toast.error('El embudo debe tener al menos una etapa');
      return;
    }

    try {
      if (editingPipeline) {
        // Update existing pipeline
        await actions.updatePipeline(editingPipeline.id, {
          name: pipelineForm.name,
          description: pipelineForm.description,
          stages: pipelineForm.stages
        });
        toast.success('Embudo actualizado exitosamente');
      } else {
        // Create new pipeline
        await actions.addPipeline({
          name: pipelineForm.name,
          description: pipelineForm.description,
          stages: pipelineForm.stages,
          isActive: true
        });
        toast.success('Embudo creado exitosamente');
      }
      setShowCreateModal(false);
      resetForm();
      setEditingPipeline(null);
    } catch (error) {
      toast.error('Error al guardar el embudo');
    }
  };

  const handleDelete = async () => {
    if (!deletingPipeline) return;
    
    // Check if there are leads in this pipeline
    const leadsInPipeline = state.leads.filter(l => l.pipelineId === deletingPipeline.id);
    if (leadsInPipeline.length > 0) {
      toast.error(`No se puede eliminar. Hay ${leadsInPipeline.length} leads en este embudo.`);
      setDeletingPipeline(null);
      return;
    }

    try {
      await actions.deletePipeline(deletingPipeline.id);
      toast.success('Embudo eliminado exitosamente');
      setDeletingPipeline(null);
    } catch (error) {
      toast.error('Error al eliminar el embudo');
    }
  };

  const getLeadsCount = (pipelineId) => {
    // Use filtered stats from API if available
    if (pipelineStats[pipelineId]) {
      return pipelineStats[pipelineId].totalLeads || 0;
    }
    return state.leads.filter(l => l.pipelineId === pipelineId).length;
  };

  const getTotalValue = (pipelineId) => {
    // Use filtered stats from API if available
    if (pipelineStats[pipelineId]) {
      return pipelineStats[pipelineId].totalValue || 0;
    }
    return state.leads
      .filter(l => l.pipelineId === pipelineId)
      .reduce((sum, l) => sum + (l.value || 0), 0);
  };

  // Get currency from settings
  const currency = settings?.currency || 'COP';

  const formatValue = (value) => {
    if (currency === 'COP') {
      if (value >= 1000000) {
        return new Intl.NumberFormat('es-CO', {
          style: 'decimal',
          minimumFractionDigits: 0,
        }).format(value / 1000000) + 'M COP';
      }
      return new Intl.NumberFormat('es-CO', {
        style: 'decimal',
        minimumFractionDigits: 0,
      }).format(value) + ' COP';
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">
              Gestión de Embudos
            </h1>
            <p className="text-lg text-slate-500">
              Crea y administra tus embudos de ventas
            </p>
          </div>
          <button
            onClick={handleOpenCreate}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-all shadow-sm font-medium"
            style={{ backgroundColor: primaryColor }}
          >
            <Plus className="w-5 h-5" />
            Nuevo Embudo
          </button>
        </div>
        
        {/* Period Filter */}
        <div className="mt-6 flex flex-wrap items-center gap-4 p-4 bg-slate-50 rounded-lg">
          <span className="text-sm font-medium text-slate-600 flex items-center gap-1">
            Periodo:
            <InfoTooltip content="Filtra las estadísticas de leads por el período seleccionado. Muestra leads creados o actualizados en el rango de fechas." />
          </span>
          <div className="relative">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
            >
              {PERIOD_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          
          {/* Custom Date Range */}
          {period === 'custom' && (
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-slate-400" />
              <input
                type="date"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-slate-400">-</span>
              <input
                type="date"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Pipelines List */}
      <div className="space-y-4">
        {state.pipelines.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <GitBranch className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              No hay embudos creados
            </h3>
            <p className="text-slate-500 mb-4">
              Crea tu primer embudo para comenzar a gestionar tus leads
            </p>
            <button
              onClick={handleOpenCreate}
              className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-all font-medium"
              style={{ backgroundColor: primaryColor }}
            >
              <Plus className="w-5 h-5" />
              Crear Embudo
            </button>
          </div>
        ) : (
          state.pipelines.map(pipeline => (
            <div
              key={pipeline.id}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              {/* Pipeline Header */}
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-indigo-100 p-3 rounded-lg">
                      <GitBranch className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900">{pipeline.name}</h3>
                      {pipeline.description && (
                        <p className="text-sm text-slate-500 mt-1">{pipeline.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Stats */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="text-slate-500">Leads</p>
                        <p className="text-xl font-bold text-slate-900">{getLeadsCount(pipeline.id)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-500">Valor Negocios</p>
                        <p className="text-xl font-bold text-green-600">{formatValue(getTotalValue(pipeline.id))}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-500">Etapas</p>
                        <p className="text-xl font-bold text-slate-900">{pipeline.stages?.length || 0}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/kanban/${pipeline.id}`)}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Ver Kanban"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleOpenEdit(pipeline)}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      {/* Delete button removed - no pipeline deletion allowed */}
                      <button
                        onClick={() => setExpandedPipeline(expandedPipeline === pipeline.id ? null : pipeline.id)}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        {expandedPipeline === pipeline.id ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Stages Preview */}
                <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2">
                  {pipeline.stages?.map((stage, index) => (
                    <React.Fragment key={stage.id}>
                      <div
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap"
                        style={{ backgroundColor: `${stage.color}20`, color: stage.color }}
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: stage.color }}
                        />
                        {stage.name}
                      </div>
                      {index < pipeline.stages.length - 1 && (
                        <span className="text-slate-300">→</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedPipeline === pipeline.id && (
                <div className="border-t border-slate-200 bg-slate-50 p-6">
                  <h4 className="font-semibold text-slate-900 mb-4">Leads por Etapa</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {pipeline.stages?.map(stage => {
                      const stageLeads = state.leads.filter(
                        l => l.pipelineId === pipeline.id && l.stageId === stage.id
                      );
                      const stageValue = stageLeads.reduce((sum, l) => sum + (l.value || 0), 0);
                      
                      return (
                        <div
                          key={stage.id}
                          className="bg-white rounded-lg p-4 border border-slate-200"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: stage.color }}
                            />
                            <span className="font-medium text-slate-900 text-sm truncate">
                              {stage.name}
                            </span>
                          </div>
                          <p className="text-2xl font-bold text-slate-900">{stageLeads.length}</p>
                          <p className="text-sm text-green-600 font-medium">{formatValue(stageValue)}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">
                {editingPipeline ? 'Editar Embudo' : 'Nuevo Embudo'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingPipeline(null);
                  resetForm();
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nombre del Embudo <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={pipelineForm.name}
                  onChange={(e) => setPipelineForm({ ...pipelineForm, name: e.target.value })}
                  placeholder="Ej: Ventas B2B, Contratación, Marketing"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Descripción (opcional)
                </label>
                <textarea
                  value={pipelineForm.description}
                  onChange={(e) => setPipelineForm({ ...pipelineForm, description: e.target.value })}
                  placeholder="Describe el propósito de este embudo..."
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 resize-none"
                />
              </div>

              {/* Stages */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-slate-700">
                    Etapas del Embudo
                  </label>
                  <button
                    onClick={handleAddStage}
                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Agregar Etapa
                  </button>
                </div>

                <div className="space-y-3">
                  {pipelineForm.stages.map((stage, index) => {
                    const isLocked = LOCKED_STAGE_IDS.includes(stage.id);
                    const isDragging = draggedStageIndex === index;
                    return (
                    <div
                      key={stage.id}
                      draggable={!isLocked}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                        isLocked ? 'bg-slate-100 border border-slate-300' : 'bg-slate-50 hover:bg-slate-100'
                      } ${isDragging ? 'opacity-50 scale-95' : ''} ${!isLocked ? 'cursor-grab active:cursor-grabbing' : ''}`}
                    >
                      <GripVertical className={`w-5 h-5 ${isLocked ? 'text-slate-300' : 'text-slate-400'}`} />
                      <span className="text-sm text-slate-500 w-6">{index + 1}</span>
                      
                      {/* Stage Name */}
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={stage.name}
                          onChange={(e) => handleStageChange(stage.id, 'name', e.target.value)}
                          placeholder="Nombre de la etapa"
                          disabled={isLocked}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 ${
                            isLocked 
                              ? 'bg-slate-200 border-slate-300 text-slate-600 cursor-not-allowed' 
                              : 'border-slate-200 bg-white'
                          }`}
                        />
                        {isLocked && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 bg-slate-300 px-2 py-0.5 rounded">
                            Obligatoria
                          </span>
                        )}
                      </div>

                      {/* Color Picker */}
                      <div className="flex items-center gap-2">
                        <Palette className="w-4 h-4 text-slate-400" />
                        <div className="flex gap-1">
                          {STAGE_COLORS.slice(0, 5).map(color => (
                            <button
                              key={color}
                              onClick={() => !isLocked && handleStageChange(stage.id, 'color', color)}
                              disabled={isLocked}
                              className={`w-6 h-6 rounded-full border-2 transition-all ${
                                stage.color === color ? 'border-slate-900 scale-110' : 'border-transparent'
                              } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Delete Stage */}
                      <button
                        onClick={() => handleRemoveStage(stage.id)}
                        disabled={isLocked}
                        className={`p-1.5 rounded transition-colors ${
                          isLocked 
                            ? 'text-slate-300 cursor-not-allowed' 
                            : 'text-slate-400 hover:text-red-600 hover:bg-red-50'
                        }`}
                        title={isLocked ? 'Etapa obligatoria - no se puede eliminar' : 'Eliminar etapa'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    );
                  })}
                </div>

                {/* Info about drag and drop */}
                <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                  <GripVertical className="w-3 h-3" />
                  Arrastra las etapas personalizadas para reordenarlas. Las etapas obligatorias no se pueden mover.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingPipeline(null);
                  resetForm();
                }}
                className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-all font-medium"
                style={{ backgroundColor: primaryColor }}
              >
                <Save className="w-4 h-4" />
                {editingPipeline ? 'Actualizar' : 'Crear Embudo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingPipeline && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Eliminar Embudo
                </h3>
                <p className="text-sm text-slate-500">
                  Esta acción no se puede deshacer
                </p>
              </div>
            </div>
            
            <p className="text-slate-700 mb-4">
              ¿Estás seguro de que deseas eliminar el embudo <strong>"{deletingPipeline.name}"</strong>?
            </p>

            {getLeadsCount(deletingPipeline.id) > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Advertencia:</strong> Este embudo tiene {getLeadsCount(deletingPipeline.id)} leads. 
                  Debes moverlos o eliminarlos antes de borrar el embudo.
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeletingPipeline(null)}
                className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={getLeadsCount(deletingPipeline.id) > 0}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineManager;
